/**
* Locale copy for /{locale}/branches.
*
* COMPLIANCE: wording must never imply that service-area points are staffed
* offices. Only the three registered offices carry a street address.
*/
export type BranchLocale = "en" | "zh" | "ja" | "ko" | "vi" | "id";

export interface BranchLocaleCopy {
  metaTitle: string;
  metaDesc: string;
  eyebrow: string;
  h1: string;
  subtitle: string;
  badges: string[];
  answer: string;
  officesHeading: string;
  areasHeading: string;
  areasIntro: string;
  mapLabel: string;
  home: string;
  self: string;
  ctaHeading: string;
  ctaBody: string;
  faq: { q: string; a: string }[];
}

const FAQ_KEYS = [
  "permanentOffice",
  "extraFee",
  "noProvince",
  "bring",
] as const;

export const BRANCH_LOCALE_COPY: Record<BranchLocale, BranchLocaleCopy> = {
  en: {
    metaTitle: "Notary Service Points Across Thailand — Nationwide Coverage",
    metaDesc:
      "Three registered offices in Bangkok, Khon Kaen and Udon Thani plus on-site appointments in every Bangkok district, BTS/MRT station, mall and all 77 provinces.",
    eyebrow: "Service network",
    h1: "Notary Public & Certified Translation Service Points in Thailand",
    subtitle:
      "Three registered law offices and hundreds of pre-booked meeting points covering every Bangkok district, transit hub, shopping mall, border checkpoint and province.",
    badges: ["Licensed Notarial Services Attorneys", "By appointment", "On-site service", "EMS nationwide"],
    answer:
      "Thai Notary Law operates three registered offices — Bangkok (Wang Thonglang), Khon Kaen and Udon Thani. Every other location listed on this page is a meeting point for on-site service that must be booked in advance; we deliberately do not publish addresses where we do not keep a staffed office.",
    officesHeading: "Registered offices",
    areasHeading: "Service areas & meeting points",
    areasIntro:
      "Book at least one business day ahead. Our attorney travels to your hotel, condominium, office or the government counter you need.",
    mapLabel: "Open in Google Maps",
    home: "Home",
    self: "Service points",
    ctaHeading: "Book an appointment",
    ctaBody: "Call +66-94-895-8999 or message LINE @Thainotary. Consultation is free.",
    faq: [
      {
        q: "Are all these locations permanent branch offices?",
        a: "No. Our registered offices with a real street address are in Bangkok (Wang Thonglang), Khon Kaen and Udon Thani. All other points listed are meeting locations for on-site service booked in advance, shown honestly so you never travel to the wrong place.",
      },
      {
        q: "Is there an extra fee for on-site service?",
        a: "The professional fee is the same as at the office. A travel charge based on actual distance is quoted and confirmed before the appointment — never added afterwards.",
      },
      {
        q: "What if my province is not on the list?",
        a: "We serve all 77 provinces. If no meeting point is near you, send documents by EMS or book a video consultation to prepare everything before signing.",
      },
      {
        q: "What should I bring to the appointment?",
        a: "Your original passport or Thai ID card, the original documents to be notarised, and — for companies — a company affidavit issued within the last six months.",
      },
    ],
  },
  zh: {
    metaTitle: "泰国公证与认证服务点 — 全国覆盖",
    metaDesc: "曼谷、孔敬、乌隆他尼三间注册办公室，并在曼谷各区、BTS/MRT 站、商场及全部 77 府提供预约上门服务。",
    eyebrow: "服务网络",
    h1: "泰国公证人与认证翻译服务点",
    subtitle:
      "三间注册律师事务所，加上覆盖曼谷各区、轨道交通枢纽、购物中心、边境口岸与全部府治的预约会面点。",
    badges: ["持牌公证律师", "需预约", "上门服务", "全国 EMS"],
    answer:
      "Thai Notary Law 拥有三间注册办公室——曼谷（旺通朗区）、孔敬与乌隆他尼。本页列出的其他地点均为需提前预约的上门服务会面点；我们不会公布没有实际办公室的地址。",
    officesHeading: "注册办公室",
    areasHeading: "服务区域与会面点",
    areasIntro: "请至少提前一个工作日预约。律师可前往您的酒店、公寓、办公室或所需的政府办事窗口。",
    mapLabel: "在 Google 地图中打开",
    home: "首页",
    self: "服务点",
    ctaHeading: "预约服务",
    ctaBody: "致电 +66-94-895-8999 或加 LINE @Thainotary，咨询免费。",
    faq: [
      {
        q: "这些地点都是常设分公司吗？",
        a: "不是。拥有真实街道地址的注册办公室位于曼谷（旺通朗区）、孔敬与乌隆他尼。其余地点均为提前预约的上门服务会面点，我们如实标注以免您跑错地方。",
      },
      { q: "上门服务是否加收费用？", a: "专业服务费与到所办理相同，另按实际距离收取交通费，会在确认预约前告知，事后不会加价。" },
      { q: "如果我的府不在名单上怎么办？", a: "我们覆盖全部 77 府。若附近没有会面点，可通过 EMS 邮寄文件或先进行视频咨询准备材料。" },
      { q: "预约当天需要带什么？", a: "护照或泰国身份证原件、需公证的原始文件；公司客户请携带六个月内的公司注册证明。" },
    ],
  },
  ja: {
    metaTitle: "タイ全土の公証・認証サービス拠点",
    metaDesc: "バンコク・コンケン・ウドンタニの登記事務所3拠点に加え、バンコク全区・BTS/MRT・モール・全77県で予約制の出張対応。",
    eyebrow: "サービスネットワーク",
    h1: "タイの公証人・認証翻訳サービス拠点",
    subtitle:
      "登記済み事務所3拠点と、バンコク全区・鉄道駅・ショッピングモール・国境検問所・全県をカバーする予約制の待ち合わせポイント。",
    badges: ["公証業務弁護士（免許保有）", "予約制", "出張対応", "全国EMS"],
    answer:
      "Thai Notary Law の登記事務所はバンコク（ワントンラン区）、コンケン、ウドンタニの3拠点です。本ページのその他の地点は事前予約制の出張サービス用の待ち合わせ場所であり、実際に事務所がない住所は掲載しません。",
    officesHeading: "登記事務所",
    areasHeading: "サービス地域・待ち合わせ場所",
    areasIntro: "少なくとも1営業日前にご予約ください。ホテル・コンドミニアム・オフィス・官庁窓口へ弁護士が伺います。",
    mapLabel: "Googleマップで開く",
    home: "ホーム",
    self: "サービス拠点",
    ctaHeading: "予約する",
    ctaBody: "+66-94-895-8999 へお電話、または LINE @Thainotary までご連絡ください。相談は無料です。",
    faq: [
      {
        q: "これらはすべて常設支店ですか？",
        a: "いいえ。実住所のある登記事務所はバンコク（ワントンラン区）、コンケン、ウドンタニの3拠点のみです。その他は事前予約の出張サービス用待ち合わせ場所で、誤ってご来訪されないよう正確に表示しています。",
      },
      { q: "出張サービスに追加料金はかかりますか？", a: "基本料金は事務所と同額で、実距離に応じた交通費のみ予約確定前にお伝えします。後から追加請求はありません。" },
      { q: "私の県が一覧にない場合は？", a: "全77県に対応しています。近くに待ち合わせ場所がない場合はEMSでの書類送付、またはビデオ相談で事前準備が可能です。" },
      { q: "当日の持ち物は？", a: "パスポートまたはタイIDの原本、公証する原本書類、法人の場合は6か月以内の会社登記証明をご持参ください。" },
    ],
  },
  ko: {
    metaTitle: "태국 전역 공증·인증 서비스 거점",
    metaDesc: "방콕·콘깬·우돈타니 등록 사무소 3곳과 방콕 전 구, BTS/MRT, 쇼핑몰, 77개 주 예약 출장 서비스.",
    eyebrow: "서비스 네트워크",
    h1: "태국 공증인 및 인증 번역 서비스 거점",
    subtitle:
      "등록 사무소 3곳과 방콕 전 구·전철역·쇼핑몰·국경 검문소·전 주를 포괄하는 예약제 미팅 포인트.",
    badges: ["공증 변호사 라이선스", "예약제", "출장 서비스", "전국 EMS"],
    answer:
      "Thai Notary Law는 방콕(왕통랑), 콘깬, 우돈타니에 등록 사무소 3곳을 운영합니다. 이 페이지의 다른 위치는 사전 예약 출장 서비스를 위한 미팅 장소이며, 실제 사무소가 없는 주소는 게시하지 않습니다.",
    officesHeading: "등록 사무소",
    areasHeading: "서비스 지역 및 미팅 장소",
    areasIntro: "최소 1영업일 전에 예약해 주세요. 호텔, 콘도, 사무실 또는 필요한 관공서 창구로 변호사가 방문합니다.",
    mapLabel: "구글 지도에서 열기",
    home: "홈",
    self: "서비스 거점",
    ctaHeading: "예약하기",
    ctaBody: "+66-94-895-8999로 전화하거나 LINE @Thainotary로 문의하세요. 상담은 무료입니다.",
    faq: [
      {
        q: "이 위치들이 모두 상설 지점인가요?",
        a: "아닙니다. 실제 주소가 있는 등록 사무소는 방콕(왕통랑), 콘깬, 우돈타니 3곳입니다. 나머지는 사전 예약 출장 서비스를 위한 미팅 장소이며 잘못 방문하시지 않도록 정확히 표기합니다.",
      },
      { q: "출장 서비스에 추가 비용이 있나요?", a: "수임료는 사무소와 동일하며 실제 거리 기준 교통비만 예약 확정 전에 안내드립니다. 사후 추가 청구는 없습니다." },
      { q: "제 주가 목록에 없으면 어떻게 하나요?", a: "77개 주 전역에 서비스합니다. 근처에 미팅 장소가 없으면 EMS로 서류를 보내거나 화상 상담으로 사전 준비가 가능합니다." },
      { q: "예약 당일 무엇을 준비해야 하나요?", a: "여권 또는 태국 신분증 원본, 공증할 원본 서류, 법인은 6개월 이내 법인등기부를 지참해 주세요." },
    ],
  },
  vi: {
    metaTitle: "Điểm dịch vụ công chứng & hợp pháp hóa toàn Thái Lan",
    metaDesc: "Ba văn phòng đăng ký tại Bangkok, Khon Kaen, Udon Thani cùng dịch vụ tận nơi theo hẹn ở mọi quận Bangkok và 77 tỉnh.",
    eyebrow: "Mạng lưới dịch vụ",
    h1: "Điểm dịch vụ công chứng viên và dịch thuật công chứng tại Thái Lan",
    subtitle:
      "Ba văn phòng luật đã đăng ký cùng hàng trăm điểm hẹn đặt trước, phủ khắp các quận Bangkok, ga tàu điện, trung tâm thương mại, cửa khẩu và mọi tỉnh thành.",
    badges: ["Luật sư công chứng có giấy phép", "Theo lịch hẹn", "Phục vụ tận nơi", "EMS toàn quốc"],
    answer:
      "Thai Notary Law có ba văn phòng đăng ký — Bangkok (Wang Thonglang), Khon Kaen và Udon Thani. Mọi địa điểm khác trên trang này là điểm hẹn cho dịch vụ tận nơi phải đặt trước; chúng tôi không công bố địa chỉ nơi không có văn phòng thực tế.",
    officesHeading: "Văn phòng đã đăng ký",
    areasHeading: "Khu vực phục vụ & điểm hẹn",
    areasIntro: "Vui lòng đặt trước ít nhất một ngày làm việc. Luật sư sẽ đến khách sạn, căn hộ, văn phòng hoặc quầy cơ quan nhà nước theo yêu cầu.",
    mapLabel: "Mở Google Maps",
    home: "Trang chủ",
    self: "Điểm dịch vụ",
    ctaHeading: "Đặt lịch hẹn",
    ctaBody: "Gọi +66-94-895-8999 hoặc nhắn LINE @Thainotary. Tư vấn miễn phí.",
    faq: [
      {
        q: "Tất cả các địa điểm này có phải chi nhánh cố định không?",
        a: "Không. Văn phòng đăng ký có địa chỉ thật nằm ở Bangkok (Wang Thonglang), Khon Kaen và Udon Thani. Các điểm còn lại là nơi hẹn gặp cho dịch vụ tận nơi đặt trước, được ghi rõ để bạn không đến nhầm chỗ.",
      },
      { q: "Dịch vụ tận nơi có phụ phí không?", a: "Phí dịch vụ giống tại văn phòng, chỉ cộng phí di chuyển theo quãng đường thực tế và luôn báo trước khi xác nhận lịch hẹn." },
      { q: "Nếu tỉnh của tôi không có trong danh sách?", a: "Chúng tôi phục vụ đủ 77 tỉnh. Nếu không có điểm hẹn gần bạn, hãy gửi hồ sơ qua EMS hoặc hẹn tư vấn video để chuẩn bị trước." },
      { q: "Cần mang theo gì khi đến hẹn?", a: "Hộ chiếu hoặc CCCD Thái bản gốc, giấy tờ gốc cần công chứng; doanh nghiệp mang giấy chứng nhận công ty cấp trong 6 tháng gần nhất." },
    ],
  },
  id: {
    metaTitle: "Titik Layanan Notaris & Legalisasi di Seluruh Thailand",
    metaDesc: "Tiga kantor terdaftar di Bangkok, Khon Kaen, dan Udon Thani plus layanan di tempat dengan janji temu di seluruh distrik Bangkok dan 77 provinsi.",
    eyebrow: "Jaringan layanan",
    h1: "Titik Layanan Notary Public dan Terjemahan Tersertifikasi di Thailand",
    subtitle:
      "Tiga kantor hukum terdaftar serta ratusan titik temu dengan janji, mencakup seluruh distrik Bangkok, stasiun BTS/MRT, mal, pos perbatasan, dan semua provinsi.",
    badges: ["Advokat notarial berlisensi", "Dengan janji temu", "Layanan di tempat", "EMS nasional"],
    answer:
      "Thai Notary Law memiliki tiga kantor terdaftar — Bangkok (Wang Thonglang), Khon Kaen, dan Udon Thani. Lokasi lain di halaman ini adalah titik temu untuk layanan di tempat yang harus dijadwalkan lebih dulu; kami tidak mencantumkan alamat tanpa kantor nyata.",
    officesHeading: "Kantor terdaftar",
    areasHeading: "Area layanan & titik temu",
    areasIntro: "Pesan minimal satu hari kerja sebelumnya. Advokat kami datang ke hotel, apartemen, kantor, atau loket instansi yang Anda butuhkan.",
    mapLabel: "Buka di Google Maps",
    home: "Beranda",
    self: "Titik layanan",
    ctaHeading: "Buat janji temu",
    ctaBody: "Telepon +66-94-895-8999 atau chat LINE @Thainotary. Konsultasi gratis.",
    faq: [
      {
        q: "Apakah semua lokasi ini kantor cabang permanen?",
        a: "Tidak. Kantor terdaftar dengan alamat jalan sebenarnya ada di Bangkok (Wang Thonglang), Khon Kaen, dan Udon Thani. Lokasi lain adalah titik temu layanan di tempat dengan janji, ditampilkan apa adanya agar Anda tidak salah datang.",
      },
      { q: "Apakah ada biaya tambahan untuk layanan di tempat?", a: "Biaya jasa sama seperti di kantor; hanya ada biaya perjalanan sesuai jarak nyata yang diinformasikan sebelum janji dikonfirmasi." },
      { q: "Bagaimana jika provinsi saya tidak ada di daftar?", a: "Kami melayani 77 provinsi. Jika tidak ada titik temu terdekat, kirim dokumen via EMS atau jadwalkan konsultasi video untuk persiapan." },
      { q: "Apa yang harus dibawa saat janji temu?", a: "Paspor atau KTP Thailand asli, dokumen asli yang akan dinotariskan, dan bagi perusahaan sertakan akta perusahaan terbitan enam bulan terakhir." },
    ],
  },
};

export const BRANCH_TYPE_LABEL_I18N: Record<BranchLocale, Record<string, string>> = {
  en: {
    hq: "Head office",
    district: "Bangkok district service area",
    province: "Provincial service area",
    mall: "Shopping mall meeting point",
    airport: "Airport meeting point",
    border: "Border checkpoint service",
    government: "Near government office",
    "visa-center": "Visa application centre",
    "expat-hub": "Expat community area",
    marina: "Marina meeting point",
    overseas: "Overseas coverage",
  },
  zh: {
    hq: "总部",
    district: "曼谷分区服务范围",
    province: "府级服务范围",
    mall: "商场会面点",
    airport: "机场会面点",
    border: "边境口岸服务",
    government: "政府机关附近",
    "visa-center": "签证申请中心",
    "expat-hub": "外籍人士社区",
    marina: "码头会面点",
    overseas: "海外覆盖",
  },
  ja: {
    hq: "本社",
    district: "バンコク区サービス地域",
    province: "県サービス地域",
    mall: "モール待ち合わせ",
    airport: "空港待ち合わせ",
    border: "国境検問所対応",
    government: "官庁近隣",
    "visa-center": "ビザ申請センター",
    "expat-hub": "外国人コミュニティ",
    marina: "マリーナ待ち合わせ",
    overseas: "海外対応",
  },
  ko: {
    hq: "본사",
    district: "방콕 구 서비스 지역",
    province: "주 서비스 지역",
    mall: "쇼핑몰 미팅 장소",
    airport: "공항 미팅 장소",
    border: "국경 검문소 서비스",
    government: "관공서 인근",
    "visa-center": "비자 신청 센터",
    "expat-hub": "외국인 커뮤니티",
    marina: "마리나 미팅 장소",
    overseas: "해외 커버리지",
  },
  vi: {
    hq: "Trụ sở chính",
    district: "Khu vực quận Bangkok",
    province: "Khu vực tỉnh",
    mall: "Điểm hẹn tại trung tâm thương mại",
    airport: "Điểm hẹn sân bay",
    border: "Dịch vụ cửa khẩu",
    government: "Gần cơ quan nhà nước",
    "visa-center": "Trung tâm tiếp nhận visa",
    "expat-hub": "Khu người nước ngoài",
    marina: "Điểm hẹn bến du thuyền",
    overseas: "Phạm vi nước ngoài",
  },
  id: {
    hq: "Kantor pusat",
    district: "Area layanan distrik Bangkok",
    province: "Area layanan provinsi",
    mall: "Titik temu mal",
    airport: "Titik temu bandara",
    border: "Layanan pos perbatasan",
    government: "Dekat kantor pemerintah",
    "visa-center": "Pusat aplikasi visa",
    "expat-hub": "Kawasan ekspatriat",
    marina: "Titik temu marina",
    overseas: "Cakupan luar negeri",
  },
};

export type { BranchLocale as BranchesLocale };
export const BRANCH_FAQ_KEYS = FAQ_KEYS;
